import { useState } from 'react';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { CreditCard } from 'lucide-react';

interface CheckoutFormProps {
  onPaymentError?: (error: string) => void;
  totalAmount?: string;
}

const CheckoutForm = ({ onPaymentError, totalAmount }: CheckoutFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleSubmit = async (event: any) => {
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      return;
    }

    setLoading(true);
    setMessage('');

    // 1. Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit();
    if (submitError) {
      const errorMsg = submitError.message || 'An unexpected error occurred.';
      setMessage(errorMsg);
      onPaymentError?.(errorMsg);
      setLoading(false);
      return;
    }
    
    // 2. Confirm the payment with the server's clientSecret
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Specify the URL to redirect back to checkout page after payment
        return_url: `${window.location.origin}${window.location.pathname}${window.location.search}`, 
      },
    });

    if (error) {
      // This point is only reached if there's an immediate error
      const errorMsg = error.message || 'An unexpected error occurred.';
      setMessage(errorMsg);
      onPaymentError?.(errorMsg);
    } else {
      // Customer is redirected to the `return_url`.
    }

    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement options={{business: {name: "Marketplace"}}} />
      <Button 
        type="submit"
        disabled={!stripe || loading}
        className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700 text-white"
        size="lg"
      >
        <CreditCard className="h-4 w-4 mr-2" />
        {loading ? "Processing..." : totalAmount ? `Pay ${totalAmount}` : "Pay Now"}
      </Button>
      {message && <div className="mt-2 text-sm text-red-600">{message}</div>}
    </form>
  );
};

export default CheckoutForm;